import React, { createContext, useState, useEffect, ReactNode } from "react";
import useAuth from "@/hooks/useAuth";
import { useStorage } from "@/hooks/useStorage";

import type { EntityPermissions, PermissionEntity } from "@/types/permissions";
import type { PermissionsContextType } from "@/types/providers/permissions";

const DEFAULT_ENTITY_PERMISSIONS: EntityPermissions = {
  create: false,
  read: false,
  update: false,
  delete: false,
};

const defaultPermissions: PermissionsContextType = {
  // Entity CRUD permissions
  projects: DEFAULT_ENTITY_PERMISSIONS,
  users: DEFAULT_ENTITY_PERMISSIONS,
  clients: DEFAULT_ENTITY_PERMISSIONS,
  contacts: DEFAULT_ENTITY_PERMISSIONS,
  invoices: DEFAULT_ENTITY_PERMISSIONS,
  estimates: DEFAULT_ENTITY_PERMISSIONS,
  expenses: DEFAULT_ENTITY_PERMISSIONS,
  incidents: DEFAULT_ENTITY_PERMISSIONS,
  investigations: DEFAULT_ENTITY_PERMISSIONS,
  courses: DEFAULT_ENTITY_PERMISSIONS,
  shifts: DEFAULT_ENTITY_PERMISSIONS,
  shift_users: DEFAULT_ENTITY_PERMISSIONS,
  shift_positions: DEFAULT_ENTITY_PERMISSIONS,
  time_cards: DEFAULT_ENTITY_PERMISSIONS,
  applications: DEFAULT_ENTITY_PERMISSIONS,
  roles: DEFAULT_ENTITY_PERMISSIONS,
  locations: DEFAULT_ENTITY_PERMISSIONS,
  unions: DEFAULT_ENTITY_PERMISSIONS,
  markets: DEFAULT_ENTITY_PERMISSIONS,
  positions: DEFAULT_ENTITY_PERMISSIONS,
  abilities: DEFAULT_ENTITY_PERMISSIONS,
  restrictions: DEFAULT_ENTITY_PERMISSIONS,
  dress_codes: DEFAULT_ENTITY_PERMISSIONS,
  billing_items: DEFAULT_ENTITY_PERMISSIONS,
  incident_types: DEFAULT_ENTITY_PERMISSIONS,
  tenant: DEFAULT_ENTITY_PERMISSIONS,
  user_emergency_contacts: DEFAULT_ENTITY_PERMISSIONS,
  user_private: DEFAULT_ENTITY_PERMISSIONS,
  user_pay: DEFAULT_ENTITY_PERMISSIONS,
  user_reviews: DEFAULT_ENTITY_PERMISSIONS,
  training: DEFAULT_ENTITY_PERMISSIONS,
  payroll: DEFAULT_ENTITY_PERMISSIONS,

  // Legacy accessors
  hasProjectsAccess: false,
  hasUsersAccess: false,
  hasClientsAccess: false,
  hasContactsAccess: false,
  hasInvoicesAccess: false,
  hasEstimatesAccess: false,
  hasPayrollAccess: false,
  hasExpensesAccess: false,
  hasIncidentsAccess: false,
  hasInvestigationsAccess: false,
  hasCoursesAccess: false,
  hasShiftsAccess: false,
  hasTimeCardsAccess: false,
  hasApplicationsAccess: false,
  hasChatAccess: false,
  isLoading: true,
};

export const PermissionsContext = createContext<PermissionsContextType>(defaultPermissions);

// Storage key prefix for caching
const STORAGE_PREFIX = "perm_";

// All entities to check permissions for
const PERMISSION_ENTITIES: PermissionEntity[] = [
  "projects",
  "users",
  "clients",
  "contacts",
  "invoices",
  "estimates",
  "expenses",
  "incidents",
  "investigations",
  "courses",
  "shifts",
  "shift_users",
  "shift_positions",
  "time_cards",
  "applications",
  "roles",
  "locations",
  "unions",
  "markets",
  "positions",
  "abilities",
  "restrictions",
  "dress_codes",
  "billing_items",
  "incident_types",
  "tenant",
  "user_emergency_contacts",
  "user_private",
  "user_pay",
  "user_reviews",
  "training",
  "payroll",
];

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { userRoles, loading: authLoading } = useAuth();
  const storage = useStorage();

  // Initialize from cache for instant render
  const [permissions, setPermissions] = useState<PermissionsContextType>(() => {
    const cached: Partial<PermissionsContextType> = { isLoading: true };

    // Load cached CRUD permissions for each entity
    for (const entity of PERMISSION_ENTITIES) {
      const cachedCreate = storage.getBoolean(`${STORAGE_PREFIX}${entity}_create`);
      const cachedRead = storage.getBoolean(`${STORAGE_PREFIX}${entity}_read`);
      const cachedUpdate = storage.getBoolean(`${STORAGE_PREFIX}${entity}_update`);
      const cachedDelete = storage.getBoolean(`${STORAGE_PREFIX}${entity}_delete`);

      (cached as any)[entity] = {
        create: cachedCreate ?? false,
        read: cachedRead ?? false,
        update: cachedUpdate ?? false,
        delete: cachedDelete ?? false,
      };
    }

    // Load legacy accessors from cache
    cached.hasProjectsAccess = storage.getBoolean(`${STORAGE_PREFIX}projects_read`) ?? false;
    cached.hasUsersAccess = storage.getBoolean(`${STORAGE_PREFIX}users_read`) ?? false;
    cached.hasClientsAccess = storage.getBoolean(`${STORAGE_PREFIX}clients_read`) ?? false;
    cached.hasContactsAccess = storage.getBoolean(`${STORAGE_PREFIX}contacts_read`) ?? false;
    cached.hasInvoicesAccess = storage.getBoolean(`${STORAGE_PREFIX}invoices_read`) ?? false;
    cached.hasEstimatesAccess = storage.getBoolean(`${STORAGE_PREFIX}estimates_read`) ?? false;
    cached.hasPayrollAccess = storage.getBoolean(`${STORAGE_PREFIX}payroll_read`) ?? false;
    cached.hasExpensesAccess = storage.getBoolean(`${STORAGE_PREFIX}expenses_read`) ?? false;
    cached.hasIncidentsAccess = storage.getBoolean(`${STORAGE_PREFIX}incidents_read`) ?? false;
    cached.hasInvestigationsAccess = storage.getBoolean(`${STORAGE_PREFIX}investigations_read`) ?? false;
    cached.hasCoursesAccess = storage.getBoolean(`${STORAGE_PREFIX}courses_read`) ?? false;
    cached.hasShiftsAccess = storage.getBoolean(`${STORAGE_PREFIX}shifts_read`) ?? false;
    cached.hasTimeCardsAccess = storage.getBoolean(`${STORAGE_PREFIX}time_cards_read`) ?? false;
    cached.hasApplicationsAccess = storage.getBoolean(`${STORAGE_PREFIX}applications_read`) ?? false;
    cached.hasChatAccess = storage.getBoolean(`${STORAGE_PREFIX}chat`) ?? false;

    return { ...defaultPermissions, ...cached };
  });

  // Update permissions when userRoles change
  useEffect(() => {
    if (authLoading) return;

    if (!userRoles) {
      // No roles = no access
      setPermissions({ ...defaultPermissions, isLoading: false });
      return;
    }

    // Helper to check specific permission for an entity
    const checkPermission = (entity: string, action: string) =>
      userRoles.some((r) => (r.permissions as any)?.[entity]?.[action] === true);

    // Build entity permissions
    const entityPerms: Record<string, EntityPermissions> = {};
    for (const entity of PERMISSION_ENTITIES) {
      entityPerms[entity] = {
        create: checkPermission(entity, "create"),
        read: checkPermission(entity, "read"),
        update: checkPermission(entity, "update"),
        delete: checkPermission(entity, "delete"),
      };
    }

    // Check chat access (has phone numbers)
    const phones = userRoles.flatMap((r) => r.permissions?.phones || []);
    const hasChatAccess = phones.length > 0;

    // Check payroll (special case - may be separate from other entities)
    const hasPayrollAccess = checkPermission("payroll", "read");

    const newPermissions: PermissionsContextType = {
      // Entity CRUD permissions
      ...(entityPerms as any),

      // Legacy accessors (derived from read permissions)
      hasProjectsAccess: entityPerms.projects.read,
      hasUsersAccess: entityPerms.users.read,
      hasClientsAccess: entityPerms.clients.read,
      hasContactsAccess: entityPerms.contacts.read,
      hasInvoicesAccess: entityPerms.invoices.read,
      hasEstimatesAccess: entityPerms.estimates.read,
      hasPayrollAccess: hasPayrollAccess,
      hasExpensesAccess: entityPerms.expenses.read,
      hasIncidentsAccess: entityPerms.incidents.read,
      hasInvestigationsAccess: entityPerms.investigations.read,
      hasCoursesAccess: entityPerms.courses.read,
      hasShiftsAccess: entityPerms.shifts.read,
      hasTimeCardsAccess: entityPerms.time_cards.read,
      hasApplicationsAccess: entityPerms.applications.read,
      hasChatAccess,
      isLoading: false,
    };

    setPermissions(newPermissions);

    // Cache to storage for instant render on next load
    for (const entity of PERMISSION_ENTITIES) {
      const perms = entityPerms[entity];
      storage.saveFast(`${STORAGE_PREFIX}${entity}_create`, perms.create);
      storage.saveFast(`${STORAGE_PREFIX}${entity}_read`, perms.read);
      storage.saveFast(`${STORAGE_PREFIX}${entity}_update`, perms.update);
      storage.saveFast(`${STORAGE_PREFIX}${entity}_delete`, perms.delete);
    }
    storage.saveFast(`${STORAGE_PREFIX}payroll_read`, hasPayrollAccess);
    storage.saveFast(`${STORAGE_PREFIX}chat`, hasChatAccess);
  }, [userRoles, authLoading, storage]);

  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>;
}
